import AlarmIcon from '@mui/icons-material/Alarm';
import SettingsIcon from '@mui/icons-material/Settings';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { AddIcon, SearchIcon } from 'assets/icons';
import { PrimaryButton } from 'components/forms/atoms/FormButtons';
import React from 'react';
import { Link } from 'react-router-dom';
import { useGetAdminBusinessUnitsQuery } from 'services/api/adminBusinessUnits.api';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  SURFACE_TINT,
} from 'styles/tokens';

const headCellSx = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.7rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: alpha(NAVY, 0.55),
  borderBottom: 'none',
};

const bodyCellSx = {
  fontFamily: 'Jost, sans-serif',
  color: NAVY,
  borderBottom: 'none',
  py: 1.25,
};

// Model list prices are USD per 1M tokens (e.g. 5, 0.15, 30) — show up to 2 decimals, no trailing zeros.
const fmtUsd = (n: number) => `US$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const actionButtonSx = {
  color: alpha(NAVY, 0.7),
  borderRadius: RADIUS_ITEM,
  transition: 'background-color 0.18s ease, color 0.18s ease',
  '&:hover': { bgcolor: SURFACE_HOVER, color: NAVY },
};

const BusinessUnits = () => {
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(null);
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredBusinessUnits = React.useMemo(() => {
    if (!businessUnits) return [];

    const filtered = [...businessUnits].filter(
      (unit) =>
        unit.company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.id.toString().includes(searchTerm)
    );

    return filtered.sort((a, b) => a.company.name.localeCompare(b.company.name));
  }, [businessUnits, searchTerm]);

  const { businessUnitsByCompanyCount, companyIndexByName } = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const idx: Record<string, number> = {};
    let next = 0;
    for (const bu of filteredBusinessUnits) {
      const name = bu.company.name;
      counts[name] = (counts[name] ?? 0) + 1;
      if (idx[name] === undefined) {
        idx[name] = next;
        next += 1;
      }
    }
    return { businessUnitsByCompanyCount: counts, companyIndexByName: idx };
  }, [filteredBusinessUnits]);

  const tbodyHoverSx = React.useMemo(() => {
    const rules: Record<string, { bgcolor: string }> = {
      // Hover any BU row → that row highlighted.
      '& tr[data-co]:hover': { bgcolor: SURFACE_HOVER },
    };
    for (const idx of Object.values(companyIndexByName)) {
      // Hover company cell → all rows of that company highlighted.
      rules[`&:has(td.company-cell[data-co="${idx}"]:hover) tr[data-co="${idx}"]`] = { bgcolor: SURFACE_HOVER };
      // Hover a non-first BU row of company → company cell on its first row highlighted.
      rules[`&:has(tr[data-co="${idx}"]:not([data-first]):hover) tr[data-co="${idx}"][data-first] td.company-cell`] = {
        bgcolor: SURFACE_HOVER,
      };
    }
    return rules;
  }, [companyIndexByName]);

  return (
    <Box sx={{ py: 3, px: { xs: 2, lg: 4 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 3,
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          autoFocus
          placeholder="Buscar por empresa o unidad de negocio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: alpha(NAVY, 0.5) }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            minWidth: { xs: '100%', sm: 360 },
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Jost, sans-serif',
              bgcolor: SURFACE_TINT,
              borderRadius: RADIUS_ITEM,
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: `1px solid ${alpha(NAVY, 0.15)}` },
            },
            '& input': { color: NAVY },
          }}
        />
        <Link to="/businessUnits/new" style={{ textDecoration: 'none' }}>
          <PrimaryButton startIcon={<AddIcon />}>NUEVA UNIDAD</PrimaryButton>
        </Link>
      </Box>
      <TableContainer
        sx={{
          height: 'calc(100vh - 140px)',
          overflow: 'auto',
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          p: 2,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Nombre empresa</TableCell>
              <TableCell sx={headCellSx}>Unidad de negocio</TableCell>
              <TableCell sx={headCellSx}>ID</TableCell>
              <TableCell sx={headCellSx}>Teléfono</TableCell>
              <TableCell sx={headCellSx}>Demochat</TableCell>
              <TableCell sx={headCellSx}>Modelo AI</TableCell>
              <TableCell sx={headCellSx} align="right">
                Costo modelo (US$/1M)
              </TableCell>
              <TableCell sx={headCellSx}>WA Tier</TableCell>
              <TableCell sx={headCellSx}>Configuración</TableCell>
              <TableCell sx={headCellSx}>Follow Ups</TableCell>
              <TableCell sx={headCellSx}>Demo Chat</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={tbodyHoverSx}>
            {filteredBusinessUnits.map((businessUnit, i) => {
              const isFirst = filteredBusinessUnits[i - 1]?.company.name !== businessUnit.company.name;
              const companyIdx = companyIndexByName[businessUnit.company.name];
              return (
                <TableRow
                  key={businessUnit.id}
                  data-co={companyIdx}
                  data-first={isFirst ? '' : undefined}
                  sx={{ transition: 'background-color 0.18s ease' }}
                >
                  {isFirst && (
                    <TableCell
                      className="company-cell"
                      data-co={companyIdx}
                      rowSpan={businessUnitsByCompanyCount[businessUnit.company.name]}
                      align="center"
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 700,
                        transition: 'background-color 0.18s ease',
                        cursor: 'default',
                      }}
                    >
                      {businessUnit.company.name}
                    </TableCell>
                  )}
                  <TableCell sx={bodyCellSx}>{businessUnit.name}</TableCell>
                  <TableCell sx={bodyCellSx}>{businessUnit.id}</TableCell>
                  <TableCell sx={bodyCellSx}>{businessUnit.phoneNumber}</TableCell>
                  <TableCell sx={bodyCellSx}>{businessUnit.pusherIdentification}</TableCell>
                  <TableCell sx={bodyCellSx}>{businessUnit.aiModel}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    {businessUnit.aiModelCost ? (
                      <>
                        <Box>
                          <Box component="span" sx={{ color: alpha(NAVY, 0.5) }}>
                            Input{' '}
                          </Box>
                          {fmtUsd(businessUnit.aiModelCost.input)}
                        </Box>
                        <Box>
                          <Box component="span" sx={{ color: alpha(NAVY, 0.5) }}>
                            Output{' '}
                          </Box>
                          {fmtUsd(businessUnit.aiModelCost.output)}
                        </Box>
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {businessUnit.phoneNumberTier
                      ?.map((tier) => `${tier.split('T')[0]} ${tier.split(':').at(-1)}`)
                      .join(', ')}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Link to={`/businessUnit-config/${businessUnit.id}`}>
                      <IconButton size="small" sx={actionButtonSx}>
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Link>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Link to={`/businessUnit-follow-up-config/${businessUnit.id}`}>
                      <IconButton size="small" sx={actionButtonSx}>
                        <AlarmIcon fontSize="small" />
                      </IconButton>
                    </Link>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Link to={`/demochat/${businessUnit.pusherIdentification}`}>
                      <IconButton size="small" sx={actionButtonSx}>
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BusinessUnits;
